import React, { useState } from 'react';
import Icon, { IconProps } from '@amzn/awsui-components-core/icon';
import Link from '@amzn/awsui-components-core/link';
import ReactMarkdown from 'react-markdown';
import { CtaButton } from '../CtaButton/CtaButton';
import { Image } from '../Image/Image';
import { VideoModal } from '../VideoModal/VideoModal';
import { StoreBadgeCta, isStoreBadgeCtaType } from '../StoreBadgeLink/StoreBadgeLink';
import { MEDIA_CENTRAL_ICONS } from '../../utils/iconRegistry';
import { blockAnchorAttributes } from '../../utils/blockAnchor';
import { hasCtaDestination } from '../../utils/ctaAction';
import type { CtaAction } from '../../utils/ctaAction';
import { markQuoteElement, stripQuoteMarks } from '../../utils/quoteMarks';
import './SuperCard.scss';

/**
 * SuperCard - Program overview card with value props and testimonials
 * 
 * Two-column layout on desktop (≥1024): left content + right testimonials
 * Stacked layout on tablet (768–1023): content top, testimonials in 2-col grid below
 * Single column on mobile (≤479): everything stacked, full-width CTA
 * 
 * Note on dual testimonials:
 * Uses flattened props (testimonialQuote, testimonialQuote2) instead of array
 * to maintain CMS compatibility. Polyphemus CMS requires flat prop structure
 * for field mapping. Limited to 2 testimonials.
 * 
 * Supports ReactMarkdown for description and value prop descriptions.
 */

/**
 * @polySemanticUsage About this block: an oversized, high-impact card that combines rich media,
 * headline, body copy, and CTA into a single prominent module. Larger and more visually dominant
 * than standard cards. Use it for featured content, spotlight stories, or primary promotional
 * callouts that need to stand apart from surrounding content.
 * Never choose SuperCard without substantive content beyond a headline and CTA:
 * populate description (body copy) and/or at least one value prop (valueProp1Heading/
 * valueProp1Description, ...). A source section that offers only a heading plus a CTA is not a
 * SuperCard — an empty-bodied SuperCard renders as a large blank shell. In particular, never
 * use a SuperCard to carry the heading or intro text around a standalone testimonial: that
 * prose belongs in a RichText block above the Testimonial.
 * A SuperCard containing an embedded testimonial or quote stays a SuperCard — do not
 * extract the quote into a standalone Testimonial block. The standalone-Testimonial detection
 * rule applies only to testimonials that are their own visual element on the page.
 * Fill testimonialQuote and testimonialQuote2 with the quote text and no surrounding quotation
 * marks of any kind (" “ ” « » „) — the component adds the marks itself in the style of the
 * page's language.
 */
export interface SuperCardProps {
  /**
   * Card heading
   * @polyLabel Headline
   */
  heading: string;
  
  /**
   * Card description (supports Markdown)
   * @polyType RichText
   * @polyHelp Description text (supports rich text formatting)
   */
  description?: string;
  
  /** Value prop 1 */
  valueProp1Heading?: string;
  valueProp1Description?: string;
  valueProp1LinkText?: string;
  /**
   * @polyType Url
   * @polyLabel Value Prop 1 Link URL
   */
  valueProp1LinkHref?: string;
  /**
   * @polyType Text
   * @polyTranslatable false
   */
  valueProp1IconName?: IconProps['name'];
  /**
   * @polyType Url
   * @polyLabel Value Prop 1 Icon URL
   */
  valueProp1IconUrl?: string;
  
  /** Value prop 2 */
  valueProp2Heading?: string;
  valueProp2Description?: string;
  valueProp2LinkText?: string;
  /**
   * @polyType Url
   * @polyLabel Value Prop 2 Link URL
   */
  valueProp2LinkHref?: string;
  /**
   * @polyType Text
   * @polyTranslatable false
   */
  valueProp2IconName?: IconProps['name'];
  /**
   * @polyType Url
   * @polyLabel Value Prop 2 Icon URL
   */
  valueProp2IconUrl?: string;
  
  /** Value prop 3 */
  valueProp3Heading?: string;
  valueProp3Description?: string;
  valueProp3LinkText?: string;
  /**
   * @polyType Url
   * @polyLabel Value Prop 3 Link URL
   */
  valueProp3LinkHref?: string;
  /**
   * @polyType Text
   * @polyTranslatable false
   */
  valueProp3IconName?: IconProps['name'];
  /**
   * @polyType Url
   * @polyLabel Value Prop 3 Icon URL
   */
  valueProp3IconUrl?: string;
  
  /** Value prop 4 */
  valueProp4Heading?: string;
  valueProp4Description?: string;
  valueProp4LinkText?: string;
  /**
   * @polyType Url
   * @polyLabel Value Prop 4 Link URL
   */
  valueProp4LinkHref?: string;
  /**
   * @polyType Text
   * @polyTranslatable false
   */
  valueProp4IconName?: IconProps['name'];
  /**
   * @polyType Url
   * @polyLabel Value Prop 4 Icon URL
   */
  valueProp4IconUrl?: string;
  
  /**
   * Optional testimonial quote, without surrounding quotation marks.
   * @polyHelp Enter the quote without quotation marks. They are added automatically in the style of the page's language.
   */
  testimonialQuote?: string;
  
  /**
   * Testimonial heading (shown when no logo)
   * @polyHelp Text heading shown when no logo is provided
   */
  testimonialHeading?: string;
  
  /**
   * Testimonial company/brand logo URL (CDN or local path)
   * @polyType Url
   */
  testimonialLogo?: string;

  /**
   * Max height for the first testimonial logo (e.g. `'14px'`). Defaults to the
   * shared cap when omitted. Use to tighten the cap for wide wordmark logos.
   * @polyHelp Override the max height for this testimonial logo (e.g. "14px")
   * @polyTranslatable false
   */
  testimonialLogoMaxHeight?: string;
  
  /** Testimonial author name */
  testimonialName?: string;
  
  /** Testimonial author title */
  testimonialTitle?: string;
  
  /**
   * Testimonial author headshot
   * @polyType Url
   */
  testimonialHeadshot?: string;
  
  /** Testimonial link text */
  testimonialLinkText?: string;
  
  /**
   * Testimonial link href
   * @polyType Url
   * @polyLabel Testimonial Link URL
   */
  testimonialLinkHref?: string;
  
  /**
   * Testimonial video URL (opens in modal when no linkHref)
   * @polyType Url
   * @polyLabel Testimonial Video URL
   * @polyHelp Video URL that opens in a modal. Only used when Testimonial Link URL is empty.
   */
  testimonialVideoUrl?: string;
  
  /**
   * Testimonial video link text
   * @polyHelp Link text shown with play icon for video modal
   */
  testimonialVideoText?: string;
  
  /**
   * Second testimonial quote, without surrounding quotation marks.
   * @polyHelp Enter the quote without quotation marks. They are added automatically in the style of the page's language.
   */
  testimonialQuote2?: string;
  
  /**
   * Second testimonial heading (shown when no logo)
   * @polyHelp Text heading shown when no logo is provided
   */
  testimonialHeading2?: string;
  
  /**
   * Second testimonial company/brand logo URL (CDN or local path)
   * @polyType Url
   */
  testimonialLogo2?: string;

  /**
   * Max height for the second testimonial logo (e.g. `'32px'`). Defaults to the
   * shared cap when omitted.
   * @polyHelp Override the max height for this testimonial logo (e.g. "32px")
   * @polyTranslatable false
   */
  testimonialLogo2MaxHeight?: string;
  
  /** Second testimonial author name */
  testimonialName2?: string;
  
  /** Second testimonial author title */
  testimonialTitle2?: string;
  
  /**
   * Second testimonial author headshot
   * @polyType Url
   */
  testimonialHeadshot2?: string;
  
  /** Second testimonial link text */
  testimonialLinkText2?: string;
  
  /**
   * Second testimonial link href
   * @polyType Url
   * @polyLabel Testimonial Link URL 2
   */
  testimonialLinkHref2?: string;
  
  /**
   * Second testimonial video URL (opens in modal when no linkHref)
   * @polyType Url
   * @polyLabel Testimonial Video URL 2
   * @polyHelp Video URL that opens in a modal. Only used when Testimonial Link URL 2 is empty.
   */
  testimonialVideoUrl2?: string;
  
  /**
   * Second testimonial video link text
   * @polyHelp Link text shown with play icon for video modal
   */
  testimonialVideoText2?: string;
  
  /**
   * Optional image URL (alternative to testimonial)
   * @polyType Url
   * @polyLabel Image Source
   */
  imageSrc?: string;
  
  /**
   * Image alt text
   * @polyLabel Image Alt Text
   */
  imageAlt?: string;
  
  /**
   * Primary CTA text
   * @polyLabel CTA Text
   */
  ctaText?: string;
  
  /**
   * Primary CTA href
   * @polyType Url
   * @polyLabel CTA Link
   */
  ctaHref?: string;

  /**
   * What the button CTA does. Ignored by store badges and the dedicated video-link type.
   * @polyType SelectList
   * @polyOptions Link=link,Watch video=video,Download=download,Popup=popup
   * @polyHelp Popup opens the Popup block matching the Target Popup ID and does not use the CTA Link field.
   * @polyDefault link
   * @polyTranslatable false
   */
  ctaAction?: CtaAction;

  /**
   * @polyHelp Match the Popup ID on the Popup block to open. Use A-Z, a-z, 0-9 and dashes; spaces become dashes, other characters are removed, and matching is case-sensitive.
   * @polyTranslatable false
   */
  ctaTargetPopupId?: string;
  
  /**
   * Primary CTA color variant
   * @polyLabel CTA Color
   */
  ctaColorVariant?: 'primary' | 'contrast' | 'secondary';
  
  /**
   * Primary CTA button ID
   * @polyLabel CTA ID
   * @polyTranslatable false
   */
  ctaId?: string;
  
  /**
   * Primary CTA button ld-append for analytics
   * @polyLabel CTA LD Appendix
   * @polyTranslatable false
   */
  ctaLdAppendix?: string;

  /**
   * CTA display type. 'button' renders a standard CtaButton; 'video-link'
   * renders a secondary CtaButton with a play icon that opens ctaHref in a video modal.
   * @polyType SelectList
   * @polyOptions Button=button,Video Link=video-link,App Store + Google Play Badges=store-badges,App Store Badge=app-store,Google Play Badge=google-play
   * @polyDefault button
   * @polyLabel CTA Type
   * @polyHelp The three badge types render the official app store artwork and need no CTA Text or CTA Link.
   */
  ctaType?: 'button' | 'video-link' | 'store-badges' | 'app-store' | 'google-play';
  /**
   * Anchor ID for in-page links: a link to `#<anchorId>` on the same page scrolls to this block.
   * Normalised to letters, numbers and dashes (spaces become dashes, a leading `#` and other
   * characters are dropped). Must be unique on the page. Migration: when any link on the source page points at `#<id>` and that id sits on the
   * source section this block replaces, copy the id here unchanged (without the `#`).
   * @polyType Text
   * @polyLabel Anchor ID
   * @polyHelp Lets in-page links such as #your-id jump to this block. Use letters, numbers and dashes (other characters are removed); must be unique on the page.
   * @polyTranslatable false
   */
  anchorId?: string;
}

export const SuperCard: React.FC<SuperCardProps> = ({
  heading,
  description,
  valueProp1Heading, valueProp1Description, valueProp1LinkText, valueProp1LinkHref, valueProp1IconName, valueProp1IconUrl,
  valueProp2Heading, valueProp2Description, valueProp2LinkText, valueProp2LinkHref, valueProp2IconName, valueProp2IconUrl,
  valueProp3Heading, valueProp3Description, valueProp3LinkText, valueProp3LinkHref, valueProp3IconName, valueProp3IconUrl,
  valueProp4Heading, valueProp4Description, valueProp4LinkText, valueProp4LinkHref, valueProp4IconName, valueProp4IconUrl,
  testimonialQuote,
  testimonialHeading,
  testimonialLogo,
  testimonialLogoMaxHeight,
  testimonialName,
  testimonialTitle,
  testimonialHeadshot,
  testimonialLinkText,
  testimonialLinkHref,
  testimonialVideoUrl,
  testimonialVideoText,
  testimonialQuote2,
  testimonialHeading2,
  testimonialLogo2,
  testimonialLogo2MaxHeight,
  testimonialName2,
  testimonialTitle2,
  testimonialHeadshot2,
  testimonialLinkText2,
  testimonialLinkHref2,
  testimonialVideoUrl2,
  testimonialVideoText2,
  imageSrc,
  imageAlt,
  ctaText,
  ctaHref,
  ctaAction,
  ctaTargetPopupId,
  ctaColorVariant,
  ctaId,
  ctaLdAppendix,
  ctaType = 'button',
  anchorId,
}) => {
  const valueProps = [
    { heading: valueProp1Heading, description: valueProp1Description, linkText: valueProp1LinkText, linkHref: valueProp1LinkHref, iconName: valueProp1IconName, iconUrl: valueProp1IconUrl },
    { heading: valueProp2Heading, description: valueProp2Description, linkText: valueProp2LinkText, linkHref: valueProp2LinkHref, iconName: valueProp2IconName, iconUrl: valueProp2IconUrl },
    { heading: valueProp3Heading, description: valueProp3Description, linkText: valueProp3LinkText, linkHref: valueProp3LinkHref, iconName: valueProp3IconName, iconUrl: valueProp3IconUrl },
    { heading: valueProp4Heading, description: valueProp4Description, linkText: valueProp4LinkText, linkHref: valueProp4LinkHref, iconName: valueProp4IconName, iconUrl: valueProp4IconUrl },
  ].filter(vp => vp.heading);

  const [videoModalUrl, setVideoModalUrl] = useState<string | null>(null);

  // Badge CTA types supply their own text and destination, so they render without the
  // authored ctaText/ctaHref pair every other SuperCard CTA type requires.
  const hasStoreBadgeCta = isStoreBadgeCtaType(ctaType);
  const resolvedCtaAction = ctaType === 'video-link' ? 'video' : ctaAction;
  const hasCta = hasStoreBadgeCta || Boolean(
    ctaText && hasCtaDestination(ctaHref, resolvedCtaAction, ctaTargetPopupId),
  );

  const renderTestimonial = (
    quote: string | undefined,
    quoteHeading: string | undefined,
    logo: string | undefined,
    logoMaxHeight: string | undefined,
    name: string | undefined,
    title: string | undefined,
    headshot: string | undefined,
    linkText: string | undefined,
    linkHref: string | undefined,
    videoUrl: string | undefined,
    videoText: string | undefined,
  ) => {
    if (!quote) return null;

    const normalizedQuote = stripQuoteMarks(quote);

    return (
      <article className="super-card__testimonial">
        <div className="super-card__testimonial-body">
          {logo ? (
            <div
              className="super-card__testimonial-logo"
              style={logoMaxHeight ? ({ '--poly-size-logo-testimonial-height': logoMaxHeight } as React.CSSProperties) : undefined}
            >
              <Image url={logo} alt="" bare />
            </div>
          ) : quoteHeading ? (
            <h3 className="super-card__testimonial-heading">{quoteHeading}</h3>
          ) : null}

          <blockquote className="super-card__quote" data-poly-quote-marks="" ref={markQuoteElement}>
            {normalizedQuote}
          </blockquote>

          <div className="super-card__testimonial-attribution">
            {headshot && (
              <div className="super-card__headshot">
                <Image url={headshot} alt={name ?? ''} variant="headshot" />
              </div>
            )}
            <div>
              {name && <div className="super-card__name">{name}</div>}
              {title && <div className="super-card__title">{title}</div>}
            </div>
          </div>
        </div>

        {linkText && linkHref ? (
          <div className="super-card__testimonial-link">
            <Link href={linkHref} variant="secondary">
              <span className="link-with-arrow">{linkText}</span>
            </Link>
          </div>
        ) : videoUrl && videoText && !linkHref ? (
          <div className="super-card__testimonial-link">
            <button
              type="button"
              className="super-card__video-link"
              onClick={() => setVideoModalUrl(videoUrl)}
            >
              {videoText}
              <Image url={MEDIA_CENTRAL_ICONS.playButton} alt="" aria-hidden="true" className="super-card__play-icon" />
            </button>
          </div>
        ) : null}
      </article>
    );
  };

  return (
    <section className="super-card" {...blockAnchorAttributes(anchorId)}>
      <div className="super-card__container">
        <div className="super-card__content">
          <div className="super-card__grid">
            {/* Left column: heading, description, value props, CTA */}
            <div className="super-card__left-column">
              <div className="super-card__header">
                <h2 className="super-card__heading">{heading}</h2>
                {description && (
                  <div className="super-card__description">
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => (
                          <Link href={href ?? ''} variant="secondary" fontSize="inherit">
                            {children}
                          </Link>
                        ),
                      }}
                    >
                      {description}
                    </ReactMarkdown>
                  </div>
                )}
              </div>

              {(valueProps.length > 0 || hasCta) && (
                <div className="super-card__value-props">
                  {valueProps.map((prop, index) => (
                    <div key={index} className="super-card__value-prop">
                      {(prop.iconName || prop.iconUrl) && (
                        <div className="super-card__value-prop-icon" aria-hidden="true">
                          {prop.iconUrl ? (
                            <Image bare url={prop.iconUrl} alt="" aria-hidden="true" />
                          ) : (
                            <Icon name={prop.iconName} size="big" />
                          )}
                        </div>
                      )}
                      <div className="super-card__value-prop-content">
                        <div className="super-card__value-prop-heading">
                          {prop.heading}
                        </div>
                        {prop.description && (
                          <p className="super-card__value-prop-description">
                            {prop.description}
                          </p>
                        )}
                        {prop.linkText && prop.linkHref && (
                          <Link href={prop.linkHref} variant="secondary">
                            <span className="link-with-arrow">{prop.linkText}</span>
                          </Link>
                        )}
                      </div>
                    </div>
                  ))}
                  
                  {hasCta && (
                    <div className="super-card__cta-wrapper">
                      {hasStoreBadgeCta ? (
                        <StoreBadgeCta ctaType={ctaType} id={ctaId} ldAppendix={ctaLdAppendix} />
                      ) : ctaType === 'video-link' ? (
                        <CtaButton
                          colorVariant="secondary"
                          size="medium"
                          href={ctaHref}
                          id={ctaId}
                          ldAppendix={ctaLdAppendix}
                          action="video"
                        >
                          {ctaText}
                        </CtaButton>
                      ) : (
                        <CtaButton
                          colorVariant={ctaColorVariant ?? 'secondary'}
                          size="medium"
                          href={ctaHref}
                          id={ctaId}
                          ldAppendix={ctaLdAppendix}
                          action={ctaAction}
                          popupTargetId={ctaTargetPopupId}
                        >
                          {ctaText}
                        </CtaButton>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right column: testimonials and/or image */}
            <div className="super-card__right-column">
              {renderTestimonial(
                testimonialQuote, testimonialHeading, testimonialLogo, testimonialLogoMaxHeight, testimonialName,
                testimonialTitle, testimonialHeadshot, testimonialLinkText, testimonialLinkHref,
                testimonialVideoUrl, testimonialVideoText,
              )}
              {renderTestimonial(
                testimonialQuote2, testimonialHeading2, testimonialLogo2, testimonialLogo2MaxHeight, testimonialName2,
                testimonialTitle2, testimonialHeadshot2, testimonialLinkText2, testimonialLinkHref2,
                testimonialVideoUrl2, testimonialVideoText2,
              )}
              {imageSrc && <Image url={imageSrc} alt={imageAlt ?? ''} />}
            </div>
          </div>
        </div>
      </div>

      {videoModalUrl && (
        <VideoModal src={videoModalUrl} onClose={() => setVideoModalUrl(null)} />
      )}
    </section>
  );
};
